import type { ListBuilderConfiguration } from '../types';

/**
 * Lead Eligibility list — surfaces every active user with their distribution
 * eligibility, capacity, and filter criteria. Row click opens the eligibility
 * editor sidebar. The "edit" inline action is the only way to mutate fields
 * (the table itself is read-only).
 *
 * The role filter is wired so the UI can default to Salesperson without the
 * config needing to know the tenant-specific role id.
 */
export const LEAD_ELIGIBILITY_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-lead-eligibility-all',
			cacheTTL: 60000,
			name: 'All Users',
			type: 'default',
			active: true,
			bulkActionTemplate: 'lead-eligibility',
			// Surface the same default the backend applies (name ASC) so the
			// sort dropdown's selected label matches the visible order.
			defaultSortId: 'column-user-name-asc',
			// Default-value rules: per-list filter defaults applied on first
			// load. `SALESPERSON` is a sentinel resolved server-side because
			// role ids differ per tenant.
			filterRules: {
				leadEligibilityIsLicensed: {
					defaultValue: [{ id: 'Yes', label: 'Licensed' }],
				},
				leadEligibilityRoles: {
					defaultValue: [{ id: 'SALESPERSON', label: 'Salesperson' }],
				},
			},
			columns: [
				{
					id: 'user-name',
					fields: [
						{
							id: 'user-name-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'user_name',
									objectTable: 'user',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'user-name-linebreak',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'user-role-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'role_names',
									objectTable: 'user',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Sales Rep' }],
					minWidth: 200,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'user_name',
							objectTable: 'user',
						},
					],
					widthConfig: { mode: 'flexible', preset: 'large' },
					verticalAlignment: 'top',
				},
				{
					id: 'is-eligible',
					fields: [
						{
							id: 'is-eligible-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'is_distribution_eligible',
									objectTable: 'user',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'boolean',
						},
					],
					headers: [{ id: 'h1', text: 'Eligible' }],
					minWidth: 100,
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'is_distribution_eligible',
							objectTable: 'user',
						},
					],
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
				{
					id: 'current-leads',
					fields: [
						{
							id: 'current-leads-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'current_leads',
									objectTable: 'user',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Current Leads' }],
					minWidth: 110,
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'current_leads',
							objectTable: 'user',
						},
					],
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
				{
					id: 'max-leads',
					fields: [
						{
							id: 'max-leads-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'max_leads',
									objectTable: 'user',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Max Leads' }],
					minWidth: 100,
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'max_leads',
							objectTable: 'user',
						},
					],
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
				{
					id: 'client-types-summary',
					fields: [
						{
							id: 'client-types-summary-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'client_types_summary',
									objectTable: 'user',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Client Types' }],
					minWidth: 140,
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'top',
				},
				{
					id: 'specialties-summary',
					fields: [
						{
							id: 'specialties-summary-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'specialties_summary',
									objectTable: 'user',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Specialties' }],
					minWidth: 140,
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'top',
				},
				{
					id: 'states-summary',
					fields: [
						{
							id: 'states-summary-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'states_summary',
									objectTable: 'user',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'States' }],
					minWidth: 140,
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'top',
				},
				{
					id: 'actions',
					headers: [{ id: 'actions-header', text: '' }],
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'edit-eligibility',
									label: 'Edit',
									iconName: 'pen',
									callbackName: 'editEligibility',
									variant: 'primary',
									visibility: 'visible',
									// PD-14923 — dedicated manage action is the sole
									// edit gate (user:update does not grant it).
									requiredPermission: 'user:lead_eligibility:manage',
								},
							],
						},
					],
					sortable: false,
					width: 80,
					align: 'center',
				},
			],
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	filters: {
		activeFilters: [
			'leadEligibilityKeywords',
			'leadEligibilityIsLicensed',
			'leadEligibilityIsEligible',
			'leadEligibilityRoles',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/settings/lead-eligibility/:user_id',
			objectLabel: 'User',
			objectTable: 'user',
			selectedFields: [
				{ fieldName: 'user_id', fieldLabel: 'ID' },
				{ fieldName: 'user_name', fieldLabel: 'Name' },
				{ fieldName: 'user_email', fieldLabel: 'Email' },
				{ fieldName: 'role_names', fieldLabel: 'Role' },
				{
					fieldName: 'is_distribution_eligible',
					fieldLabel: 'Distribution Eligible',
				},
				{ fieldName: 'current_leads', fieldLabel: 'Current Leads' },
				{ fieldName: 'max_leads', fieldLabel: 'Max Leads' },
				{ fieldName: 'client_types_summary', fieldLabel: 'Client Types' },
				{ fieldName: 'specialties_summary', fieldLabel: 'Specialties' },
				{ fieldName: 'states_summary', fieldLabel: 'States' },
			],
		},
	],
};
